/** @format */

import {
	Breadcrumb,
	KillSwitches,
	ProcessedNonBundleProduct,
	ProductListSEO,
	ProductSku
} from '@shared/data-access/interfaces';
import {
	ChangeDetectorRef,
	Component,
	inject,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Signal,
	ViewChild,
	viewChild,
	ViewContainerRef
} from '@angular/core';
import { DeviceService, UtilityService } from '@shared/data-access/services';
import {
	outOfStockTemplateInitialized,
	selectKillswitch,
	selectPrimaryCategory,
	selectProductCatalogRatingCount,
	selectProductCatalogRatingValue
} from '@pdp/data-access/state';

import { BreakPointNew } from '@shared/data-access/enums';
import { DynamicProductTemplate } from '@pdp/data-access/enums';
import { Store as NgRxStore } from '@ngrx/store';
import { ReviewHighlyRatedFeaturesModalComponent } from '@product-reviews/components/review-highly-rated-features-modal';
import { Subscription } from 'rxjs';
import { chain } from '@core/chain';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { take } from 'rxjs/operators';
import { DynamicProductTemplateClass } from '../shared/dynamic-product-template-class.interface';
import {
	BackToSearchResultsComponent,
	BreadcrumbComponent,
	ProductDescriptionComponent,
	ProductImageCarouselComponent,
	ProductInfoSpecsComponent,
	TechSpecsComponent
} from '@pdp/components';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';
import { GoogleAdManagerComponent, ProductRecommendationsComponent } from '@shared/components';
import { ReviewBVReviewsComponent } from '@product-reviews/components/review-bv-reviews';
import { ReviewHighlightContainerComponent } from '@product-reviews/components/review-highlight-container';
import { ReviewHighlyRatedFeaturesComponent } from '@product-reviews/components/review-highly-rated-features';
import { ReviewMultimediaCountComponent } from '@product-reviews/components/review-multimedia-count';
import { ReviewQuestionsAndAnswersV2Component } from '@product-reviews/components/review-questions-and-answers-v2';
import { ReviewRatingSummaryStarsComponent } from '@product-reviews/components/review-rating-summary-stars';
import { ProductAnalyticsService } from '@shared/data-access/analytics';

@Component({
	selector: 'pdp-templates-out-of-stock-default',
	templateUrl: './out-of-stock-default.component.html',
	styleUrl: './out-of-stock-default.component.scss',
	imports: [
		BackToSearchResultsComponent,
		BreadcrumbComponent,
		CommonModule,
		HmfButtonComponent,
		ProductDescriptionComponent,
		ProductImageCarouselComponent,
		ProductInfoSpecsComponent,
		ProductRecommendationsComponent,
		ReviewBVReviewsComponent,
		ReviewHighlightContainerComponent,
		ReviewHighlyRatedFeaturesComponent,
		ReviewHighlyRatedFeaturesModalComponent,
		ReviewMultimediaCountComponent,
		ReviewQuestionsAndAnswersV2Component,
		ReviewRatingSummaryStarsComponent,
		TechSpecsComponent,
		GoogleAdManagerComponent
	]
})
export class OutOfStockDefaultComponent
	implements
		DynamicProductTemplateClass<DynamicProductTemplate.OUT_OF_STOCK_DEFAULT>,
		OnInit,
		OnDestroy
{
	private readonly utilityService = inject(UtilityService);
	protected readonly platformId = inject(PLATFORM_ID);
	private readonly deviceService = inject(DeviceService);
	private readonly ngRxstore = inject(NgRxStore);
	private readonly changeDetectorRef = inject(ChangeDetectorRef);
	private readonly analyticsService = inject(ProductAnalyticsService);

	/**
	 * @description
	 * Reference to the view position where the above the fold content will be placed
	 */
	@ViewChild('targetAboveTheFoldViewRef', { read: ViewContainerRef })
	targetAboveTheFoldViewRef: ViewContainerRef;

	highlyRatedFeaturesModal: Signal<ReviewHighlyRatedFeaturesModalComponent> = viewChild.required(
		'highlyRatedFeaturesModal'
	);

	/**
	 * @description
	 * Reference to the initial view position of the section before it is moved into this
	 * component's template
	 */
	@Input() sourceAboveTheFoldViewRef: ViewContainerRef;

	/**
	 * @description
	 * The product currently being displayed
	 */
	@Input() product: ProcessedNonBundleProduct;

	/**
	 * @description
	 * The kill switches
	 */
	@Input() killSwitches: KillSwitches;

	resolvedSku: ProductSku;

	/**
	 * @description
	 * The current chain
	 */
	chain = chain;

	/**
	 * @description
	 * The currently selected color attribute value if applicable
	 */
	selectedColorAttribute: string;

	/**
	 * @description
	 * Determines whether to show the info summary on product description
	 */
	infoSummaryEnabled = this.ngRxstore.selectSignal(selectKillswitch('infoSummaryEnabled'));

	/**
	 * @description
	 * feature flag for breadcrumbs
	 */
	browseBreadcrumbsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('browseBreadcrumbsEnabled')
	);

	primaryCategoryBreadcrumb: string;

	oosBreadCrumbData: Breadcrumb;

	/**
	 * @description
	 * If it's mobile viewport
	 * NOTE: Please don't initialize this flag. The undefined value is used for loading
	 * mobile components in SSR and then hidden them in the browser using media queries
	 * when the viewport is for desktop
	 */
	isMobile: boolean;

	/**
	 * @description
	 * Feature flag review widget.
	 */
	reviewsOff = this.ngRxstore.selectSignal(selectKillswitch('reviewsOff'));

	/**
	 * @description
	 * Feature flag new reviews widget.
	 */
	newReviews = this.ngRxstore.selectSignal(selectKillswitch('newReviews'));
	readonly useProductCatalogRatingData = this.ngRxstore.selectSignal(
		selectKillswitch('useProductCatalogRatingData')
	);

	reviewsMVPRetest = this.ngRxstore.selectSignal(selectKillswitch('reviewsMVPRetest'));

	isLoadingStateRecsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isLoadingStateRecsEnabled')
	);
	isReviewImgPlacementEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isReviewImgPlacementEnabled')
	);

	oosImageUrl = '';
	analyticsEventSent = false;

	private subscriptions = new Subscription();

	/**
	 * @description
	 * replaces the isPlatformBrowser(this.platformId) function call
	 */
	isPlatformBrowser = false;
	displayableDescriptiveAttributes = [];
	useAboveTheFoldMobileContainer = this.ngRxstore.selectSignal(
		selectKillswitch('useAboveTheFoldMobileContainer')
	);
	wasAboveTheFoldContentLoaded = false;

	/**
	 * @description
	 * Feature flag to show the top 3 highly rated features
	 */
	isHighlyRatedReviewsEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isHighlyRatedReviewsEnabled')
	);

	readonly isRibbonGAMBannerEnabled = this.ngRxstore.selectSignal(
		selectKillswitch('isRibbonGAMBannerEnabled')
	);

	readonly ratingCount = this.ngRxstore.selectSignal(selectProductCatalogRatingCount);
	readonly ratingValue = this.ngRxstore.selectSignal(selectProductCatalogRatingValue);

	readonly templateName = DynamicProductTemplate.OUT_OF_STOCK_DEFAULT as const;

	ngOnInit() {
		this.isPlatformBrowser = isPlatformBrowser(this.platformId);

		this.setBreadCrumbData();

		this.oosImageUrl = `https://dks.scene7.com/is/image/GolfGalaxy/${this.product.partNumber}?wid=382&hei=382&fmt=pjpeg`;

		if (this.isPlatformBrowser) {
			const breakpoint = this.deviceService.getDeviceBreakPointNew();
			this.isMobile = breakpoint <= BreakPointNew.SMALL;

			// Ensure there is something that was set
			if (typeof this.product !== 'undefined' && this.product !== null) {
				// Default the selected color attribute
				this.selectedColorAttribute = this.product.defaultColor;
			}
			this.updateDisplayableAttributes();
		}

		// sets inventory and promotion data to empty when OOS template is loaded so analytics events are fired in setProductDataEventsObservableForAnalytics
		this.ngRxstore.dispatch(outOfStockTemplateInitialized());
	}

	ngAfterViewInit(): void {
		// When the app is loaded in the browser we want to move the Above the Fold content that is initially rendered
		// in the product container by detaching it and then inserting it into this component's template without destroying the instance.
		if (
			isPlatformBrowser(this.platformId) &&
			this.useAboveTheFoldMobileContainer() &&
			this.sourceAboveTheFoldViewRef
		) {
			const view = this.sourceAboveTheFoldViewRef.detach();
			// We use this flag to determine if the Above the Fold content was completely moved and remove
			// the temporary styles used for adding a placeholder of the end position of the Above the Fold content
			this.wasAboveTheFoldContentLoaded = true;
			this.targetAboveTheFoldViewRef.insert(view);
			// Fire change detection for wasAboveTheFoldContentLoaded flag
			this.changeDetectorRef.detectChanges();
		}
	}

	imageError() {
		this.oosImageUrl =
			'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';
	}

	imageLoad() {
		if (
			typeof this.product !== 'undefined' &&
			this.product !== null &&
			this.oosImageUrl &&
			!this.analyticsEventSent
		) {
			this.analyticsService.sendAttentiveHeroImage(this.oosImageUrl, this.product.partNumber);
			this.analyticsEventSent = true;
		}
	}

	setBreadCrumbData() {
		this.subscriptions.add(
			this.ngRxstore
				.select(selectPrimaryCategory)
				.pipe(take(5))
				.subscribe((primaryCategory: ProductListSEO) => {
					this.primaryCategoryBreadcrumb = primaryCategory?.identifier;

					if (
						primaryCategory?.breadCrumbDetails &&
						Array.isArray(primaryCategory?.breadCrumbDetails)
					) {
						this.oosBreadCrumbData =
							primaryCategory.breadCrumbDetails[
								primaryCategory.breadCrumbDetails.length - 1
							];
					}
				})
		);
	}

	get oosCtaText(): string {
		return `SHOP ALL ${this.oosBreadCrumbData?.name}`;
	}

	public setDisplayableAttributes() {
		const { descriptiveAttributes } = this.product;

		if (!Array.isArray(descriptiveAttributes) || descriptiveAttributes.length === 0) {
			return [];
		}

		return descriptiveAttributes.filter((attr) => {
			const isDisplayable = this.utilityService.isTruthy(attr?.isDisplayable);
			const isProTips = attr.name.toLowerCase() === 'dsg pro tips';
			const shouldIncludeProTips = isProTips && this.chain.proTipsFlag;
			const shouldIncludeNonProTips = !isProTips;

			return isDisplayable && (shouldIncludeProTips || shouldIncludeNonProTips);
		});
	}

	public updateDisplayableAttributes(): void {
		this.displayableDescriptiveAttributes = this.setDisplayableAttributes();
	}

	openHighlyRatedFeatures() {
		this.highlyRatedFeaturesModal()?.openHighlyRatedFeatures();
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}
}
